import React, { useEffect, useState, useRef, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { 
  FaExclamationTriangle, 
  FaCar, 
  FaMapMarkerAlt, 
  FaUser, 
  FaWalking,
  FaLocationArrow,
  FaExpandAlt
} from "react-icons/fa";
import { AuthContext } from '../../Provider';
import { useToast } from '../../hooks/ToastContext';
import { CustomButton3 } from '../../Templates';
import { parsePointCoordinates } from '../utils/geocoding';
import PostDetailsModal from '../../PostDetailsModal';
import Spinner from '../../common/Spinner';
import "../styles/EventMapView.css";

/**
 * Component that displays event location, drivers, and passengers on a map
 * 
 * @param {Object} props
 * @param {Object} props.event - The event data
 * @param {Array} props.rides - Array of ride offers
 * @param {Array} props.rideRequests - Array of ride requests
 * @param {Function} props.onViewRide - Function to view ride details
 * @param {Function} props.onViewRequest - Function to view request details
 * @param {Function} props.onMessageUser - Function to message a user
 */
const EventMapView = ({ 
  event, 
  rides = [], 
  rideRequests = [], 
  onViewRide, 
  onViewRequest,
  onMessageUser 
}) => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const infoWindowRef = useRef(null);
  const boundsRef = useRef(null);
  
  // State
  const [map, setMap] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [showFullMapView, setShowFullMapView] = useState(false);
  
  // Post details modal state
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [modalPostId, setModalPostId] = useState(null);
  const [modalPostType, setModalPostType] = useState('post');
  const [mapMarkers, setMapMarkers] = useState({
    event: null,
    drivers: [],
    passengers: []
  });

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setIsMapLoading(false);
      } else {
        console.warn("Google Maps API not loaded yet, waiting...");
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  // Clean up markers and polylines when component unmounts
  useEffect(() => {
    return () => {
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Clear all markers
      clearAllMarkers();
    };
  }, []);
  
  // Helper to clear all markers and polylines
  const clearAllMarkers = () => {
    // Clear event marker
    if (mapMarkers.event) {
      mapMarkers.event.setMap(null);
    }
    
    // Clear driver markers
    mapMarkers.drivers.forEach(marker => {
      if (marker) marker.setMap(null);
    });
    
    // Clear passenger markers
    mapMarkers.passengers.forEach(marker => {
      if (marker) marker.setMap(null);
    });
    
    // Clear all polylines
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    
    // Reset the markers state
    setMapMarkers({
      event: null,
      drivers: [],
      passengers: []
    });
    
    // Clear the polylines ref
    polylinesRef.current = [];
  };

  // Initialize map
  useEffect(() => {
    if (isMapLoading || mapInitialized || !mapRef.current || !event) return;

    try {
      console.log("Initializing Google Maps for event map view...");

      // Use event location coordinates if available, otherwise default to Latvia
      let eventLocation = { lat: 56.9677, lng: 24.1056 }; // Default to Latvia
      
      // Create map instance first
      const mapOptions = {
        center: eventLocation,
        zoom: 10, // Default zoom level - we'll adjust this later
        fullscreenControl: false,
        mapTypeControl: true,
        streetViewControl: false,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      };
      
      const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
      setMap(newMap);
      
      // Initialize bounds for auto-zooming
      boundsRef.current = new window.google.maps.LatLngBounds();
      
      // Then handle event location
      if (event.location_coordinates) {
        const parsedCoords = parsePointCoordinates(event.location_coordinates);
        if (parsedCoords) {
          eventLocation = parsedCoords;
          newMap.setCenter(eventLocation);
          addEventMarker(newMap, eventLocation);
          
          // Add event location to bounds
          boundsRef.current.extend(new window.google.maps.LatLng(
            eventLocation.lat,
            eventLocation.lng
          ));
        } else {
          // Geocode if we couldn't parse coordinates
          geocodeEventLocation(newMap, event.location);
        }
      } else if (event.location) {
        // Geocode the location string
        geocodeEventLocation(newMap, event.location);
      } else {
        // If no location data, add event marker at default location
        addEventMarker(newMap, eventLocation);
        
        // Add default location to bounds
        boundsRef.current.extend(new window.google.maps.LatLng(
          eventLocation.lat,
          eventLocation.lng
        ));
      }
      
      setMapInitialized(true);
      console.log("Map initialized successfully for event map view");
    } catch (e) {
      console.error("Error initializing map:", e);
      setError(t('errors.map_initialization_failed'));
    }
  }, [isMapLoading, event, t]);
  
  // Geocode event location and add marker
  const geocodeEventLocation = (mapInstance, locationString) => {
    if (!locationString || !window.google || !window.google.maps) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: locationString }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        mapInstance.setCenter(location);
        
        // Add event marker
        addEventMarker(mapInstance, {
          lat: location.lat(),
          lng: location.lng()
        });
        
        // Add event location to bounds
        boundsRef.current.extend(location);
      } else {
        console.warn(`Geocoding failed for event location: ${locationString}`, status);
        // Add marker at default location if geocoding fails
        const defaultLocation = { lat: 56.9677, lng: 24.1056 };
        addEventMarker(mapInstance, defaultLocation);
        
        // Add default location to bounds
        boundsRef.current.extend(new window.google.maps.LatLng(
          defaultLocation.lat,
          defaultLocation.lng
        ));
      }
    });
  };

  // Add event marker to map
  const addEventMarker = (mapInstance, position) => {
    if (!mapInstance || !position) {
      console.error("Cannot add event marker - missing map or position", { mapInstance, position });
      return;
    }
    
    console.log("Adding event marker at position:", position);
    
    // Clear previous event marker if it exists
    if (mapMarkers.event) {
      mapMarkers.event.setMap(null);
    }
    
    // Create marker for event location - USING CUSTOM EVENT MARKER
    const eventMarker = new window.google.maps.Marker({
      position,
      map: mapInstance,
      title: event.title,
      icon: {
        url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
        scaledSize: new window.google.maps.Size(40, 40) // Make event marker larger
      },
      zIndex: 999 // Make sure event marker is on top
    });
    
    // Add info window for event
    const infoContent = `
      <div class="map-info-window event-info">
        <h3>${event.title}</h3>
        <p><strong>${t('events.location')}:</strong> ${event.location}</p>
        <p><strong>${t('events.date')}:</strong> ${new Date(event.start_date).toLocaleDateString()}</p>
        <p><strong>${t('events.time')}:</strong> ${new Date(event.start_date).toLocaleTimeString(undefined, { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}</p>
      </div>
    `;
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: infoContent
    });
    
    // Add click listener using function keyword to preserve 'this'
    eventMarker.addListener('click', function() {
      console.log("Event marker clicked");
      
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Open this info window
      infoWindow.open(mapInstance, this);
      infoWindowRef.current = infoWindow;
    });
    
    // Update the markers state
    setMapMarkers(prev => ({
      ...prev,
      event: eventMarker
    }));
  };

  // Add drivers and passengers to map when rides or rideRequests change
  useEffect(() => {
    if (!map || !mapInitialized) return;
    
    console.log("Updating ride and passenger markers");
    console.log("Rides to display:", rides.length, rides);
    console.log("Ride requests to display:", rideRequests.length, rideRequests);
    
    // Clear existing driver and passenger markers, but keep event marker
    mapMarkers.drivers.forEach(marker => {
      if (marker) marker.setMap(null);
    });
    
    mapMarkers.passengers.forEach(marker => {
      if (marker) marker.setMap(null);
    });
    
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    
    // Reset the drivers and passengers arrays in state
    setMapMarkers(prev => ({
      ...prev,
      drivers: [],
      passengers: []
    }));
    
    // Clear the polylines ref
    polylinesRef.current = [];
    
    // Reset bounds with event marker position
    boundsRef.current = new window.google.maps.LatLngBounds();
    
    // Add event marker position to bounds if it exists
    if (mapMarkers.event) {
      const eventPosition = mapMarkers.event.getPosition();
      boundsRef.current.extend(eventPosition);
    }
    
    // Process ride offers (drivers)
    const newDriverMarkers = [];
    if (rides && rides.length > 0) {
      rides.forEach(ride => {
        processRideOffer(ride, newDriverMarkers);
      });
    }
    
    // Process ride requests (passengers)
    const newPassengerMarkers = [];
    if (rideRequests && rideRequests.length > 0) {
      rideRequests.forEach(request => {
        processRideRequest(request, newPassengerMarkers);
      });
    }
    
    // Update the markers state with the new markers
    setMapMarkers(prev => ({
      ...prev,
      drivers: newDriverMarkers,
      passengers: newPassengerMarkers
    }));
    
    // Auto-zoom to fit all markers after a small delay to ensure all markers are added
    setTimeout(() => {
      if (boundsRef.current && !boundsRef.current.isEmpty() && map) {
        console.log("Auto-zooming map to fit all markers");
        
        // Check if bounds has only a single point
        const ne = boundsRef.current.getNorthEast();
        const sw = boundsRef.current.getSouthWest();
        
        if (ne.equals(sw)) {
          console.log("Bounds has only a single point, using default zoom level");
          map.setCenter(ne);
          map.setZoom(12);
        } else {
          console.log("Fitting map to bounds:", boundsRef.current);
          map.fitBounds(boundsRef.current);
          
          // Add a bit of padding
          const listener = window.google.maps.event.addListenerOnce(map, 'bounds_changed', function() {
            const currentZoom = map.getZoom();
            if (currentZoom > 15) {
              map.setZoom(15); // Max zoom level
            } else if (currentZoom < 9) {
              map.setZoom(9); // Minimum zoom level
            }
          });
        }
      }
    }, 500);
    
  }, [map, mapInitialized, rides, rideRequests, event, user, t, onViewRide, onMessageUser]);
  
  // Process a ride offer and add marker
  const processRideOffer = (ride, markersArray) => {
    try {
      console.log("Processing ride:", ride);
      
      // Get position from coordinates or geocode
      let position = null;
      
      if (ride.departure_coordinates) {
        position = parsePointCoordinates(ride.departure_coordinates);
      }
      
      // If we couldn't parse coordinates, use geocoding as fallback
      if (!position && ride.departure_location && window.google && window.google.maps) {
        console.log("No coordinates, geocoding departure location:", ride.departure_location);
        
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: ride.departure_location }, (results, status) => {
          if (status === 'OK' && results[0] && map) {
            const location = results[0].geometry.location;
            
            // Add location to bounds
            boundsRef.current.extend(location);
            
            // Create driver marker with geocoded position
            const marker = createDriverMarker({
              lat: location.lat(),
              lng: location.lng()
            }, ride);
            
            if (marker) {
              markersArray.push(marker);
              
              // Update the state manually since we're in an async callback
              setMapMarkers(prev => ({
                ...prev,
                drivers: [...prev.drivers, marker]
              }));
            }
          } else {
            console.error("Geocoding failed for:", ride.departure_location, status);
          }
        });
        return;
      }
      
      if (!position) {
        console.log("No valid position for ride:", ride);
        return;
      }
      
      // Add location to bounds
      boundsRef.current.extend(new window.google.maps.LatLng(
        position.lat,
        position.lng
      ));
      
      // Create the driver marker
      const marker = createDriverMarker(position, ride);
      if (marker) {
        markersArray.push(marker);
      }
      
    } catch (markerError) {
      console.error("Error adding driver marker:", markerError, ride);
    }
  };
  
  // Create a driver marker
  const createDriverMarker = (position, ride) => {
    if (!position || !ride) return null;
    
    // Determine if this is the current user's ride
    const isUserRide = ride.driver_id === user?.id;
    console.log("Creating driver marker:", position, "isUserRide:", isUserRide);
    
    // Create marker for driver
    const driverMarker = new window.google.maps.Marker({
      position,
      map,
      title: `${ride.driver_profile?.profiles[0]?.username || 'Driver'} - ${ride.departure_location || ''}`,
      icon: {
        url: isUserRide ? "https://maps.google.com/mapfiles/ms/icons/orange-dot.png" : "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
      },
      zIndex: isUserRide ? 998 : 100 // Put user's rides above others
    });
    
    // Create info window content
    // Make sure we have a valid post_id before setting it as a data attribute
    const postId = ride.post_id || '';
    
    const infoContent = `
      <div class="map-info-window driver-info">
        <h3>${ride.driver_profile?.profiles[0]?.username || 'Driver'}</h3>
        <p><strong>${t('events.from')}:</strong> ${ride.departure_location || ''}</p>
        <p><strong>${t('events.departure_time')}:</strong> ${new Date(ride.departure_time || new Date()).toLocaleTimeString(undefined, { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}</p>
        <p><strong>${t('posts.seats_available')}:</strong> ${ride.seats_available || 0}/${ride.seats_offered || 0}</p>
        <div class="info-actions">
          ${postId ? `<button class="info-button view-ride" data-ride-id="${postId}">${t('events.view_ride')}</button>` : ''}
          ${!isUserRide && ride.driver_id ? `<button class="info-button message-user" data-user-id="${ride.driver_id}">${t('events.send_message')}</button>` : ''}
        </div>
      </div>
    `;
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: infoContent
    });
    
    // Add click listener directly with an onClick function for buttons
    driverMarker.addListener('click', function() {
      console.log("Driver marker clicked");
      
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Open this info window
      infoWindow.open(map, this);
      infoWindowRef.current = infoWindow;
      
      // Add event listeners for buttons after a short delay
      setTimeout(() => {
        console.log("Adding button event listeners for driver info window");
        
        // Debug - log all buttons found
        const driverInfoButtons = document.querySelectorAll('.info-button');
        console.log(`Found ${driverInfoButtons.length} info buttons in total`);
        
        // Find all view ride buttons and add click handlers
        const viewButtons = document.querySelectorAll('.view-ride');
        console.log(`Found ${viewButtons.length} view-ride buttons`);
        viewButtons.forEach(button => {
          console.log('Adding click handler to view-ride button with id:', button.getAttribute('data-ride-id'));
          
          // Clear any existing event listeners
          const newButton = button.cloneNode(true);
          button.parentNode.replaceChild(newButton, button);
          
          // Add new click handler
          newButton.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            const rideId = this.getAttribute('data-ride-id');
            console.log("View ride clicked for ride ID:", rideId);
            // Make sure rideId exists and is valid
            if (rideId && rideId !== 'undefined' && rideId !== 'null') {
            console.log('Calling onViewRide with ID:', rideId);
            // Close the info window before navigating
              if (infoWindowRef.current) {
              infoWindowRef.current.close();
              }
              // Instead of using the passed callback, we'll open the modal directly
                setModalPostId(rideId);
                setModalPostType('post');
                setIsPostModalOpen(true);
              } else {
              console.error('Invalid ride ID found in button attribute:', rideId);
            }
          });
        });
        
        // Find all message user buttons and add click handlers
        const messageButtons = document.querySelectorAll('.message-user');
        messageButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const userId = button.getAttribute('data-user-id');
            console.log("Message user clicked for user ID:", userId);
            onMessageUser(userId);
          });
        });
      }, 300);
    });
    
    // Draw route to event if coordinates are available
    if (map && event.location_coordinates) {
      try {
        const eventCoords = parsePointCoordinates(event.location_coordinates);
        
        if (eventCoords) {
          // Draw polyline between driver location and event
          const polyline = new window.google.maps.Polyline({
            path: [position, eventCoords],
            geodesic: true,
            strokeColor: isUserRide ? '#FF9800' : '#1976D2',
            strokeOpacity: 0.7,
            strokeWeight: 3
          });
          
          polyline.setMap(map);
          polylinesRef.current.push(polyline);
        }
      } catch (routeError) {
        console.error("Error drawing route:", routeError);
      }
    }
    
    return driverMarker;
  };
  
  // Process a ride request and add marker
  const processRideRequest = (request, markersArray) => {
    try {
      console.log("Processing ride request:", request);
      
      // Get position from coordinates or geocode
      let position = null;
      
      if (request.pickup_coordinates) {
        console.log("Passenger has coordinates:", request.pickup_coordinates);
        position = parsePointCoordinates(request.pickup_coordinates);
        console.log("Parsed passenger coordinates:", position);
      }
      
      // If we couldn't parse coordinates, use geocoding as fallback
      if (!position && request.pickup_location && window.google && window.google.maps) {
        console.log("No coordinates, geocoding pickup location:", request.pickup_location);
        
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: request.pickup_location }, (results, status) => {
          if (status === 'OK' && results[0] && map) {
            const location = results[0].geometry.location;
            
            // Add location to bounds
            boundsRef.current.extend(location);
            
            // Create passenger marker with geocoded position
            const marker = createPassengerMarker({
              lat: location.lat(),
              lng: location.lng()
            }, request);
            
            if (marker) {
              markersArray.push(marker);
              
              // Update the state manually since we're in an async callback
              setMapMarkers(prev => ({
                ...prev,
                passengers: [...prev.passengers, marker]
              }));
            }
          } else {
            console.error("Geocoding failed for:", request.pickup_location, status);
          }
        });
        return;
      }
      
      if (!position) {
        console.log("No valid position for request:", request);
        return;
      }
      
      // Add location to bounds
      boundsRef.current.extend(new window.google.maps.LatLng(
        position.lat,
        position.lng
      ));
      
      // Create the passenger marker
      const marker = createPassengerMarker(position, request);
      if (marker) {
        markersArray.push(marker);
      }
      
    } catch (markerError) {
      console.error("Error adding passenger marker:", markerError, request);
    }
  };
  
  // Create a passenger marker
  const createPassengerMarker = (position, request) => {
    if (!position || !request) return null;
    
    // Determine if this is the current user's request
    const isUserRequest = request.passenger_id === user?.id;
    console.log("Creating passenger marker:", position, "isUserRequest:", isUserRequest);
    
    // Create marker for passenger
    const passengerMarker = new window.google.maps.Marker({
      position,
      map,
      title: `${request.passenger_profile?.profiles[0]?.username || 'Passenger'} - ${request.pickup_location}`,
      icon: {
        url: isUserRequest ? "https://maps.google.com/mapfiles/ms/icons/green-dot.png" : "https://maps.google.com/mapfiles/ms/icons/pink-dot.png"
      },
      zIndex: isUserRequest ? 997 : 50 // Priority for display
    });
    
    // Extract post_id from notes if available
    let postId = null;
    if (request.notes && request.notes.includes('Post ID:')) {
      const postIdMatch = request.notes.match(/Post ID: (\d+)/);
      if (postIdMatch && postIdMatch[1]) {
        postId = postIdMatch[1];
      }
    }
    
    // Create info window content
    const infoContent = `
      <div class="map-info-window passenger-info">
        <h3>${request.passenger_profile?.profiles[0]?.username || 'Passenger'}</h3>
        <p><strong>${t('events.pickup_location')}:</strong> ${request.pickup_location}</p>
        ${request.notes ? `<p><strong>${t('events.notes')}:</strong> ${request.notes.split(' | Post ID:')[0]}</p>` : ''}
        <div class="info-actions">
          <button class="info-button view-request" data-request-id="${request.id}">${t('events.view_request')}</button>
          ${!isUserRequest ? `<button class="info-button message-user" data-user-id="${request.passenger_id}">${t('events.send_message')}</button>` : ''}
        </div>
      </div>
    `;
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: infoContent
    });
    
    // Add click listener using function keyword instead of arrow function
    passengerMarker.addListener('click', function() {
      console.log("Passenger marker clicked");
      
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Open this info window
      infoWindow.open(map, this);
      infoWindowRef.current = infoWindow;
      
      // Add event listeners for buttons after a short delay
      setTimeout(() => {
        console.log("Adding button event listeners for passenger info window");
        
        // Debug - log all buttons found in passenger info window
        const passengerInfoButtons = document.querySelectorAll('.info-button');
        console.log(`Found ${passengerInfoButtons.length} info buttons in total`);
        
        // Find all view request buttons in driver info and add click handlers
        const driverViewRequestButtons = document.querySelectorAll('.view-request');
        console.log(`Found ${driverViewRequestButtons.length} view-request buttons in driver info`);
        driverViewRequestButtons.forEach(button => {
          console.log('Adding click handler to view-request button with id:', button.getAttribute('data-request-id'));
          
          // Clear any existing event listeners
          const newButton = button.cloneNode(true);
          button.parentNode.replaceChild(newButton, button);
          
          // Add new click handler
          newButton.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            const requestId = this.getAttribute('data-request-id');
            console.log("View request clicked for request ID:", requestId);
            // Make sure requestId exists and is valid
            if (requestId && requestId !== 'undefined' && requestId !== 'null') {
            console.log('Opening modal for request with ID:', requestId);
            // Close the info window before showing modal
            if (infoWindowRef.current) {
            infoWindowRef.current.close();
            }
            // Set modal state to open the details
                setModalPostId(requestId);
                setModalPostType('request');
                setIsPostModalOpen(true);
              } else {
              console.error('Invalid request ID found in button attribute:', requestId);
            }
          });
        });
        
        // Define the view details handler function
        const viewDetailsClickHandler = function(e) {
          e.preventDefault();
          e.stopPropagation();
          console.log("View details clicked for passenger");
          
          // Create a temporary div to show more details
          const infoDiv = document.createElement('div');
          infoDiv.className = 'expanded-passenger-details';
          infoDiv.innerHTML = `
            <h4>${request.passenger_profile?.profiles[0]?.username || 'Passenger'}</h4>
            <p><strong>Pickup Location:</strong> ${request.pickup_location}</p>
            ${request.pickup_coordinates ? `<p><strong>Coordinates:</strong> ${request.pickup_coordinates}</p>` : ''}
            ${request.notes ? `<p><strong>Notes:</strong> ${request.notes.split(' | Post ID:')[0]}</p>` : ''}
            <p><strong>Status:</strong> ${request.status}</p>
            <p><strong>Created:</strong> ${new Date(request.created_at).toLocaleString()}</p>
            ${request.waypoints && request.waypoints.length > 0 ? 
              `<p><strong>Waypoints:</strong> ${request.waypoints.join(', ')}</p>` : ''}
          `;
          
          // Replace the info window content with expanded details
          const infoContainer = this.closest('.map-info-window');
          if (infoContainer) {
            const originalContent = infoContainer.innerHTML;
            infoContainer.innerHTML = '';
            infoContainer.appendChild(infoDiv);
            
            // Add a back button
            const backButton = document.createElement('button');
            backButton.className = 'info-button back-button';
            backButton.textContent = 'Back';
            backButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              infoContainer.innerHTML = originalContent;
              
              // Reattach event listeners
              setTimeout(() => {
                // Reattach all event listeners
                const newViewDetailsButtons = document.querySelectorAll('.view-details');
                newViewDetailsButtons.forEach(btn => {
                  btn.addEventListener('click', viewDetailsClickHandler);
                });
                
                const newViewButtons = document.querySelectorAll('.view-ride');
                newViewButtons.forEach(btn => {
                  btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const rideId = btn.getAttribute('data-ride-id');
                    console.log("View ride clicked for ride ID:", rideId);
                    if (infoWindowRef.current) {
                    infoWindowRef.current.close();
                  }
                  setModalPostId(rideId);
                  setModalPostType('post');
                  setIsPostModalOpen(true);
                  });
                });
                
                const newRequestButtons = document.querySelectorAll('.view-request');
                newRequestButtons.forEach(btn => {
                  btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const requestId = btn.getAttribute('data-request-id');
                    console.log("View request clicked for request ID:", requestId);
                    if (infoWindowRef.current) {
                      infoWindowRef.current.close();
                    }
                    setModalPostId(requestId);
                    setModalPostType('request');
                    setIsPostModalOpen(true);
                  });
                });
                
                const newMessageButtons = document.querySelectorAll('.message-user');
                newMessageButtons.forEach(btn => {
                  btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const userId = btn.getAttribute('data-user-id');
                    console.log("Message user clicked for user ID:", userId);
                    onMessageUser(userId);
                  });
                });
              }, 10);
            });
            infoDiv.appendChild(backButton);
          }
        };
        
        // Find all view ride buttons and add click handlers
        const viewButtons = document.querySelectorAll('.view-ride');
        viewButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const rideId = button.getAttribute('data-ride-id');
            console.log("View ride clicked for ride ID:", rideId);
            // Make sure rideId exists and is valid
            if (rideId && rideId !== 'undefined' && rideId !== 'null') {
              onViewRide(rideId);
            } else {
              console.error('Invalid ride ID found in button attribute:', rideId);
            }
          });
        });
        
        // Find all view request buttons in passenger info and add click handlers
        const passengerViewRequestButtons = document.querySelectorAll('.view-request');
        console.log(`Found ${passengerViewRequestButtons.length} view-request buttons in passenger info`);
        passengerViewRequestButtons.forEach(button => {
          console.log('Adding click handler to view-request button with id:', button.getAttribute('data-request-id'));
          
          // Clear any existing event listeners
          const newButton = button.cloneNode(true);
          button.parentNode.replaceChild(newButton, button);
          
          // Add new click handler
          newButton.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            const requestId = this.getAttribute('data-request-id');
            console.log("View request clicked for request ID:", requestId);
            // Make sure requestId exists and is valid
            if (requestId && requestId !== 'undefined' && requestId !== 'null') {
              console.log('Opening modal for request with ID:', requestId);
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
              }
              setModalPostId(requestId);
              setModalPostType('request');
              setIsPostModalOpen(true);
            } else {
              console.error('Invalid request ID found in button attribute:', requestId);
            }
          });
        });
        
        // Find all message user buttons and add click handlers
        const messageButtons = document.querySelectorAll('.message-user');
        messageButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const userId = button.getAttribute('data-user-id');
            console.log("Message user clicked for user ID:", userId);
            onMessageUser(userId);
          });
        });
      }, 300);
    });
    
    return passengerMarker;
  };

  // Toggle full view
  const toggleFullView = () => {
    setShowFullMapView(!showFullMapView);
    
    // Trigger resize event to ensure map renders correctly
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      
      // Re-center map
      recenterMap();
    }, 300);
  };

  // Recenter map function
  const recenterMap = () => {
    if (!map || !event) return;
    
    try {
      // Check if we have a valid bounds with multiple points
      if (boundsRef.current && !boundsRef.current.isEmpty()) {
        console.log("Recentering map to fit all markers");
        
        // Check if bounds has only a single point
        const ne = boundsRef.current.getNorthEast();
        const sw = boundsRef.current.getSouthWest();
        
        if (ne.equals(sw)) {
          console.log("Bounds has only a single point, using default zoom level");
          map.setCenter(ne);
          map.setZoom(12);
        } else {
          console.log("Fitting map to bounds:", boundsRef.current);
          map.fitBounds(boundsRef.current);
          
          // Add a bit of padding
          const listener = window.google.maps.event.addListenerOnce(map, 'bounds_changed', function() {
            const currentZoom = map.getZoom();
            if (currentZoom > 15) {
              map.setZoom(15); // Max zoom level
            } else if (currentZoom < 9) {
              map.setZoom(9); // Minimum zoom level
            }
          });
        }
        return;
      }
      
      // If no valid bounds, try event coordinates
      if (event.location_coordinates) {
        const eventCoords = parsePointCoordinates(event.location_coordinates);
        if (eventCoords) {
          map.setCenter(eventCoords);
          map.setZoom(12);
          return;
        }
      }
      
      // If no event coordinates, try to find the current event marker position
      if (mapMarkers.event) {
        map.setCenter(mapMarkers.event.getPosition());
        map.setZoom(12);
      }
      // If no event marker, default to Latvia
      else {
        map.setCenter({ lat: 56.9677, lng: 24.1056 });
        map.setZoom(10);
      }
    } catch (e) {
      console.error("Error re-centering map:", e);
    }
  };

  // Display loading state
  if (isMapLoading) {
    return (
      <div className="event-map-loading">
        <Spinner size="medium" customText={t('app.loading_map')} />
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="event-map-error">
        <FaExclamationTriangle />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`event-map-view ${showFullMapView ? 'full-view' : ''}`}>
      <div className="event-map-header">
        <h3>
          <FaMapMarkerAlt /> {t('events.ride_map')}
        </h3>
        <div className="event-map-actions">
          <button 
            className="recenter-button map-action-button" 
            onClick={recenterMap}
            title={t('events.recenter_map')}
          >
            <FaLocationArrow />
          </button>
          <button 
            className="expand-button map-action-button" 
            onClick={toggleFullView}
            title={showFullMapView ? t('events.minimize_map') : t('events.maximize_map')}
          >
            <FaExpandAlt />
          </button>
        </div>
      </div>
      
      <div className="event-map-container" ref={mapRef}></div>
      
      <div className="map-legend">
        <div className="legend-item">
          <div className="legend-marker event-marker"></div>
          <span>{t('events.event_location')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-marker driver-marker"></div>
          <span>{t('events.driver')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-marker user-driver-marker"></div>
          <span>{t('events.your_ride')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-marker passenger-marker"></div>
          <span>{t('events.passenger')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-marker user-passenger-marker"></div>
          <span>{t('events.your_request')}</span>
        </div>
      </div>
      
      {showFullMapView && (
        <div className="close-fullview-button">
          <CustomButton3
            label={t('buttons.close')}
            onClick={toggleFullView}
          />
        </div>
      )}
      
      {/* Post Details Modal */}
      <PostDetailsModal
        isOpen={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
        id={modalPostId}
        type={modalPostType}
      />
    </div>
  );
};

export default EventMapView;